import { makeStyles } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import Typography from '~components/typography';
import { BOOKING_CATEGORY, BOOKING_STATUS } from '~graphql-types';
import { BasicInstitution, BasicLaptop, BasicPhoneFilled, SettingsEmail } from '~svg-components';
import { FORMAT_DAY_DATE_MONTH_YEAR } from '~utils/date-format';
import BookingStatusChip from '../booking/booking-details/booking-status-chip';
import ExternalIdChip from './external-id-chip';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: 'flex',
      alignItems: 'stretch',
      minWidth: '360px',
      padding: '12px 12px 8px 16px',
      minHeight: '72px',
      marginBottom: '12px',
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
      border: `1px solid ${theme.colors.greyE8}`,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.colors.greyFa,
      },
    },

    bookingStatusLabel: {
      ...theme.typography.body2,
      padding: '0 8px',
    },

    otherBookingInfo: {
      ...theme.flex.crossCenter,
    },

    mainBookingInfo: {
      display: 'block',
      marginRight: '8px',
      flex: 1,
    },

    nameCategoryAndBed: {
      transform: 'translateY(-2px)',
    },

    username: {
      display: 'inline',
      marginRight: '8px',
      wordBreak: 'break-all',
      verticalAlign: 'bottom',
    },

    bookingCategoryIcon: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 20,
      height: 20,
      backgroundColor: theme.colors.greyE8,
      borderRadius: '50%',
      color: theme.colors.grey66,
      marginRight: '8px',
      transform: 'translateY(2px)',
      '& > svg': {
        width: '14px',
        height: '14px',
      },
    },

    emailIcon: {
      ...theme.flex.center,
      width: 16,
      height: 16,
      borderRadius: '50%',
      marginRight: '4px',
      backgroundColor: theme.colors.grey99,
      color: theme.colors.greyFa,
      '& > svg': {
        width: '10px',
        height: '10px',
      },
    },

    phoneIcon: {
      ...theme.flex.center,
      width: 16,
      height: 16,
      borderRadius: '50%',
      marginRight: '4px',
      marginLeft: '8px',
      backgroundColor: theme.colors.grey99,
      color: theme.colors.greyFa,
      '& > svg': {
        width: '10px',
        height: '10px',
      },
    },

    selectionBedNormal: {
      display: 'inline-block',
      backgroundColor: theme.colors.greyE8,
      borderRadius: theme.borderRadius.huge,
      padding: '0 8px',
      verticalAlign: 'text-bottom',
      transform: 'translateY(2px)',
    },

    selectionBedResident: {
      display: 'inline-block',
      backgroundColor: theme.colors.grey66,
      borderRadius: theme.borderRadius.huge,
      padding: '0 8px',
      verticalAlign: 'text-bottom',
    },

    divider: {
      margin: '0 8px',
    },

    tagAndChip: {
      ...theme.flex.col,
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      minWidth: '60px',
      textAlign: 'right',
    },

    chip: {
      width: '100%',
    },
  }),
  { name: 'BookingCard' }
);

function BookingCard({ booking }) {
  const classes = useStyles();
  const {
    studentFullName,
    bedName,
    bookingStatus,
    tenancyOptionName,
    tenancyStartDate,
    tenancyEndDate,
    bookingCategory,
    bookingId,
    originId,
    customFieldValues,
    studentEmail,
    studentMobilePhone,
  } = booking;
  const renderStudentName = () => (
    <Typography variant="body1" themeColor="dark" className={classes.username}>
      {studentFullName}
    </Typography>
  );

  const renderBookingCategoryTag = () => (
    <div className={classes.bookingCategoryIcon}>
      {bookingCategory === BOOKING_CATEGORY.NOMINATION ? <BasicInstitution /> : <BasicLaptop />}
    </div>
  );

  const renderSelectionBedNormal = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.selectionBedNormal}>
      {bedName}
    </Typography>
  );

  const renderSelectionBedResident = () => (
    <Typography variant="body2" themeColor="white" className={classes.selectionBedResident}>
      {bedName}
    </Typography>
  );

  const renderSelectionBed = () =>
    bookingStatus === BOOKING_STATUS.RESIDENT
      ? renderSelectionBedResident()
      : renderSelectionBedNormal();

  const renderTenancyOptionName = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.tenancyOption}>
      {tenancyOptionName || '-'}
    </Typography>
  );

  const renderDivider = () => (
    <Typography variant="body1" themeColor="greyE8" className={classes.divider}>
      |
    </Typography>
  );

  const renderCheckInCheckOut = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.tenancyOption}>
      {`${format(parseISO(tenancyStartDate), FORMAT_DAY_DATE_MONTH_YEAR)} - ${format(
        parseISO(tenancyEndDate),
        FORMAT_DAY_DATE_MONTH_YEAR
      )}`}
    </Typography>
  );

  const renderNameCategoryAndBed = () => (
    <div className={classes.nameCategoryAndBed}>
      {renderStudentName()}
      {renderBookingCategoryTag()}
      {renderSelectionBed()}
    </div>
  );

  const renderTenancy = () => (
    <div className={classes.otherBookingInfo}>
      {renderTenancyOptionName()}
      {tenancyOptionName && tenancyStartDate && tenancyEndDate && renderDivider()}
      {tenancyStartDate && tenancyEndDate && renderCheckInCheckOut()}
    </div>
  );

  const renderEmailAndPhone = () => (
    <div className={classes.otherBookingInfo}>
      <div className={classes.emailIcon}>
        <SettingsEmail />
      </div>
      <Typography variant="body2" themeColor="grey66">
        {studentEmail || '-'}
      </Typography>
      <div className={classes.phoneIcon}>
        <BasicPhoneFilled />
      </div>
      <Typography variant="body2" themeColor="grey66">
        {studentMobilePhone || '-'}
      </Typography>
    </div>
  );

  const renderMainBookingInfo = () => (
    <div className={classes.mainBookingInfo}>
      {renderNameCategoryAndBed()}
      {renderEmailAndPhone()}
      {renderTenancy()}
    </div>
  );

  const renderBookingStatusTagAndChip = () => (
    <div className={classes.tagAndChip}>
      <BookingStatusChip status={bookingStatus} classes={{ label: classes.bookingStatusLabel }} />
      <ExternalIdChip className={classes.chip} externalIds={customFieldValues} cardId={originId} />
    </div>
  );

  const openBookingDetailPage = () => {
    const bookingDetailsUrl = queryString.stringifyUrl({
      url: '/booking/booking-details',
      query: { originBookingId: JSON.parse(atob(bookingId))?.id },
    });
    window.open(bookingDetailsUrl);
  };

  return (
    <div
      className={classes.root}
      onClick={() => {
        openBookingDetailPage();
      }}
    >
      {renderMainBookingInfo()}
      {renderBookingStatusTagAndChip()}
    </div>
  );
}

BookingCard.prototype = {
  booking: PropTypes.object.isRequired,
};

export default BookingCard;
